import type { Track } from '../track/TrackLoader';
import type { GhostStartState } from './Ghost';
import { sampleTrainingGhostState } from './TrainingGhost';

export interface AdaptiveTrainingRabbitOptions {
  desiredLeadMeters?: number;
  minLeadMeters?: number;
  maxLeadMeters?: number;
  leashLeadMeters?: number;
  respawnLeadMeters?: number;
  maxSpeedMetersPerSecond?: number;
  responsePerSecond?: number;
  passBackResponsePerSecond?: number;
  accelerationMetersPerSecondSq?: number;
  decelerationMetersPerSecondSq?: number;
  stuckSpeedMetersPerSecond?: number;
  stuckGraceSeconds?: number;
  stuckRespawnSeconds?: number;
  offSurfaceSpeedScale?: number;
}

export interface AdaptiveTrainingRabbitUpdate {
  dt: number;
  playerProgress: number;
  playerSpeed: number;
  wrongWay: boolean;
  onRacingSurface: boolean;
}

const DEFAULT_DESIRED_LEAD_METERS = 30;
const DEFAULT_MIN_LEAD_METERS = 8;
const DEFAULT_MAX_LEAD_METERS = 62;
const DEFAULT_LEASH_LEAD_METERS = 105;
const DEFAULT_RESPAWN_LEAD_METERS = 34;
const DEFAULT_MAX_SPEED_METERS_PER_SECOND = 98;
const DEFAULT_RESPONSE_PER_SECOND = 1.7;
const DEFAULT_PASS_BACK_RESPONSE_PER_SECOND = 2.7;
const DEFAULT_ACCELERATION_METERS_PER_SECOND_SQ = 66;
const DEFAULT_DECELERATION_METERS_PER_SECOND_SQ = 95;
const DEFAULT_STUCK_SPEED_METERS_PER_SECOND = 4.5;
const DEFAULT_STUCK_GRACE_SECONDS = 0.3;
const DEFAULT_STUCK_RESPAWN_SECONDS = 1.2;
const DEFAULT_OFF_SURFACE_SPEED_SCALE = 0.35;
const DELTA_REFERENCE_SPEED_METERS_PER_SECOND = 30;
const DELTA_LIMIT_MS = 9990;

/**
 * A training-only rabbit controller. It follows centerline progress with an
 * elastic gap target instead of replaying a fixed lap, so new players get a
 * useful thing to chase without fake PB/leaderboard timing.
 */
export class AdaptiveTrainingRabbit {
  private readonly desiredLeadMeters: number;
  private readonly minLeadMeters: number;
  private readonly maxLeadMeters: number;
  private readonly leashLeadMeters: number;
  private readonly respawnLeadMeters: number;
  private readonly maxSpeedMetersPerSecond: number;
  private readonly responsePerSecond: number;
  private readonly passBackResponsePerSecond: number;
  private readonly accelerationMetersPerSecondSq: number;
  private readonly decelerationMetersPerSecondSq: number;
  private readonly stuckSpeedMetersPerSecond: number;
  private readonly stuckGraceSeconds: number;
  private readonly stuckRespawnSeconds: number;
  private readonly offSurfaceSpeedScale: number;
  private progress = 0;
  private speed = 0;
  private stalledSeconds = 0;

  constructor(
    private readonly track: Track,
    options: AdaptiveTrainingRabbitOptions = {},
  ) {
    this.desiredLeadMeters = positive(options.desiredLeadMeters, DEFAULT_DESIRED_LEAD_METERS);
    this.minLeadMeters = Math.min(
      this.desiredLeadMeters,
      positive(options.minLeadMeters, DEFAULT_MIN_LEAD_METERS),
    );
    this.maxLeadMeters = Math.max(
      this.desiredLeadMeters,
      positive(options.maxLeadMeters, DEFAULT_MAX_LEAD_METERS),
    );
    this.leashLeadMeters = Math.max(
      this.maxLeadMeters + 1,
      positive(options.leashLeadMeters, DEFAULT_LEASH_LEAD_METERS),
    );
    this.respawnLeadMeters = clamp(
      positive(options.respawnLeadMeters, DEFAULT_RESPAWN_LEAD_METERS),
      this.minLeadMeters,
      this.maxLeadMeters,
    );
    this.maxSpeedMetersPerSecond = positive(
      options.maxSpeedMetersPerSecond,
      DEFAULT_MAX_SPEED_METERS_PER_SECOND,
    );
    this.responsePerSecond = positive(options.responsePerSecond, DEFAULT_RESPONSE_PER_SECOND);
    this.passBackResponsePerSecond = positive(
      options.passBackResponsePerSecond,
      DEFAULT_PASS_BACK_RESPONSE_PER_SECOND,
    );
    this.accelerationMetersPerSecondSq = positive(
      options.accelerationMetersPerSecondSq,
      DEFAULT_ACCELERATION_METERS_PER_SECOND_SQ,
    );
    this.decelerationMetersPerSecondSq = positive(
      options.decelerationMetersPerSecondSq,
      DEFAULT_DECELERATION_METERS_PER_SECOND_SQ,
    );
    this.stuckSpeedMetersPerSecond = positive(
      options.stuckSpeedMetersPerSecond,
      DEFAULT_STUCK_SPEED_METERS_PER_SECOND,
    );
    this.stuckGraceSeconds = positive(options.stuckGraceSeconds, DEFAULT_STUCK_GRACE_SECONDS);
    this.stuckRespawnSeconds = Math.max(
      this.stuckGraceSeconds,
      positive(options.stuckRespawnSeconds, DEFAULT_STUCK_RESPAWN_SECONDS),
    );
    this.offSurfaceSpeedScale = clamp(
      finiteOr(options.offSurfaceSpeedScale, DEFAULT_OFF_SURFACE_SPEED_SCALE),
      0,
      1,
    );
  }

  reset(playerProgress = 0): GhostStartState {
    this.progress = nonNegative(playerProgress) + this.desiredLeadMeters;
    this.speed = 0;
    this.stalledSeconds = 0;
    return this.currentState();
  }

  update(update: AdaptiveTrainingRabbitUpdate): GhostStartState {
    const dt = clamp(finiteOr(update.dt, 0), 0, 0.1);
    if (dt === 0) return this.currentState();

    const playerProgress = nonNegative(update.playerProgress);
    const playerSpeed = nonNegative(update.playerSpeed);
    const playerConstrained = playerSpeed <= this.stuckSpeedMetersPerSecond
      || update.wrongWay
      || !update.onRacingSurface;
    this.stalledSeconds = playerConstrained ? this.stalledSeconds + dt : 0;

    let gap = this.progress - playerProgress;
    if (this.stalledSeconds >= this.stuckRespawnSeconds && gap > this.leashLeadMeters) {
      this.progress = playerProgress + this.respawnLeadMeters;
      this.speed = 0;
      gap = this.progress - playerProgress;
    }

    if (gap < -this.leashLeadMeters) {
      this.progress = playerProgress - this.respawnLeadMeters;
      this.speed = Math.min(this.maxSpeedMetersPerSecond, Math.max(this.speed, playerSpeed));
      gap = this.progress - playerProgress;
    }

    const response = gap < 0 ? this.passBackResponsePerSecond : this.responsePerSecond;
    let targetSpeed = playerSpeed + (this.desiredLeadMeters - gap) * response;
    if (gap > this.maxLeadMeters) targetSpeed = 0;

    if (this.stalledSeconds >= this.stuckGraceSeconds) {
      targetSpeed = gap > this.minLeadMeters ? 0 : Math.min(targetSpeed, this.stuckSpeedMetersPerSecond);
    } else if (update.wrongWay) {
      targetSpeed = 0;
    } else if (!update.onRacingSurface) {
      targetSpeed *= this.offSurfaceSpeedScale;
    }

    targetSpeed = clamp(targetSpeed, 0, this.maxSpeedMetersPerSecond);
    const maxDelta = (targetSpeed >= this.speed
      ? this.accelerationMetersPerSecondSq
      : this.decelerationMetersPerSecondSq) * dt;
    this.speed = approach(this.speed, targetSpeed, maxDelta);
    this.progress += this.speed * dt;
    return this.currentState();
  }

  getProgress(): number {
    return this.progress;
  }

  getSpeed(): number {
    return this.speed;
  }

  getStalledSeconds(): number {
    return this.stalledSeconds;
  }

  getGap(playerProgress: number): number {
    return this.progress - nonNegative(playerProgress);
  }

  getDeltaMs(playerProgress: number, playerSpeed: number): number {
    const referenceSpeed = Math.max(
      DELTA_REFERENCE_SPEED_METERS_PER_SECOND,
      nonNegative(playerSpeed),
      this.speed,
    );
    return clamp(this.getGap(playerProgress) / referenceSpeed * 1000, -DELTA_LIMIT_MS, DELTA_LIMIT_MS);
  }

  private currentState(): GhostStartState {
    return sampleTrainingGhostState(this.track, this.progress, this.speed);
  }
}

function approach(value: number, target: number, maxDelta: number): number {
  if (value < target) return Math.min(target, value + maxDelta);
  return Math.max(target, value - maxDelta);
}

function positive(value: number | undefined, fallback: number): number {
  return value !== undefined && Number.isFinite(value) && value > 0 ? value : fallback;
}

function finiteOr(value: number | undefined, fallback: number): number {
  return value !== undefined && Number.isFinite(value) ? value : fallback;
}

function nonNegative(value: number): number {
  return Number.isFinite(value) ? Math.max(0, value) : 0;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
